<template>
  <div
    class="base-color-picker"
    :class="rootClasses"
  >
    <div class="base-color-picker__row">
      <BaseScrollHorizontal class="base-color-picker__scroll">
        <div class="base-color-picker__swatches">
          <button
            v-for="(color, index) in stripList"
            :key="`${color}-${index}`"
            type="button"
            class="base-color-picker__swatch"
            :class="{
              'base-color-picker__swatch--selected': isSelected(color),
              'base-color-picker__swatch--light': isLightHex(color),
            }"
            :style="{ '--swatch': color }"
            :tabindex="props.tabindex"
            :aria-label="color"
            @click="handleSelect(color)"
          />
        </div>
      </BaseScrollHorizontal>
      <div v-if="dropdownList.length > 0" class="base-color-picker__more">
        <BaseDropdownWrapper
          placement="bottom-end"
          max-height="min(320px, 60vh)"
          strategy="fixed"
          width="fill"
          with-rounded
        >
          <template #control="{ isOpen: isDropdownOpen }">
            <button
              type="button"
              class="base-color-picker__more-btn"
              :tabindex="props.tabindex"
              aria-haspopup="listbox"
              :aria-expanded="isDropdownOpen"
            >
              <span
                class="base-color-picker__more-arrow"
                :class="{ 'base-color-picker__more-arrow--open': isDropdownOpen }"
              >
                <BaseIcon
                  name="vuesax:arrow-down"
                  :size="moreArrowIconSize"
                  color="var(--color-foreground-regular)"
                />
              </span>
            </button>
          </template>
          <template #dropdown>
            <div class="base-color-picker__palette" role="listbox">
              <button
                v-for="(color, index) in dropdownList"
                :key="`all-${color}-${index}`"
                type="button"
                class="base-color-picker__palette-item"
                :class="{
                  'base-color-picker__swatch--selected': isSelected(color),
                  'base-color-picker__swatch--light': isLightHex(color),
                }"
                :style="{ '--swatch': color }"
                role="option"
                :aria-selected="isSelected(color)"
                @click="handleSelect(color)"
              />
            </div>
          </template>
        </BaseDropdownWrapper>
      </div>
    </div>
    <BaseTypography
      v-if="props.errorMessage"
      class="base-color-picker__error"
      type="text-mini-alt"
      color="var(--color-foreground-danger)"
    >
      {{ props.errorMessage }}
    </BaseTypography>
  </div>
</template>

<script setup lang="ts">
import { WORKSPACE_MODULE_COLORS } from '~/shared/const/workspaceModuleUi'
import BaseDropdownWrapper from '../dropdown-wrapper/index.vue'
import BaseIcon from '../icon/index.vue'
import BaseScrollHorizontal from '../scroll-horizontal/index.vue'
import BaseTypography from '../typography/index.vue'

interface Props {
  name?: string
  tabindex?: number
  variant?: 'light' | 'secondary'
  size?: 'md' | 'lg'
  /** true — круглые ячейки и подложка-капсула (полукруглые торцы); false — ячейки с rem(8), подложка с лёгким скруглением */
  rounded?: boolean
  /** Сколько цветов в горизонтальной полоске; в дропдауне — полный список `colors` */
  stripCount?: number
  colors?: readonly string[]
  errorMessage?: string | boolean
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'light',
  size: 'md',
  rounded: false,
  stripCount: 10,
  colors: () => [...WORKSPACE_MODULE_COLORS],
})

const modelValue = defineModel<string>({ required: true })

const stripList = computed(() => {
  const n = Math.max(0, Math.floor(props.stripCount))
  return [...props.colors].slice(0, n)
})

const dropdownList = computed(() => [...props.colors])

const rootClasses = computed(() => ({
  [`base-color-picker--variant-${props.variant}`]: true,
  [`base-color-picker--size-${props.size}`]: true,
  'base-color-picker--rounded': props.rounded,
}))

const moreArrowIconSize = computed(() => {
  if (props.size === 'lg') {
    return props.rounded ? '22px' : '24px'
  }

  return props.rounded ? '18px' : '20px'
})

function normalizeHex(hex: string) {
  return hex.trim().toLowerCase()
}

function isSelected(color: string) {
  return normalizeHex(modelValue.value ?? '') === normalizeHex(color)
}

function isLightHex(hex: string) {
  const h = hex.replace('#', '')
  if (h.length === 3) {
    const r = Number.parseInt(h[0]! + h[0]!, 16)
    const g = Number.parseInt(h[1]! + h[1]!, 16)
    const b = Number.parseInt(h[2]! + h[2]!, 16)
    return (r + g + b) / 3 > 200
  }
  if (h.length === 6) {
    const r = Number.parseInt(h.slice(0, 2), 16)
    const g = Number.parseInt(h.slice(2, 4), 16)
    const b = Number.parseInt(h.slice(4, 6), 16)
    return (r + g + b) / 3 > 200
  }
  return false
}

function handleSelect(color: string) {
  modelValue.value = color.trim()
}
</script>

<style lang="scss" scoped>
.base-color-picker {
  width: 100%;

  &__row {
    display: flex;
    align-items: center;
    gap: rem(8px);
    width: 100%;
    border-radius: rem(8px);
    padding: rem(8px);
  }

  &__scroll {
    flex: 1 1 auto;
    min-width: 0;
  }

  &__swatches {
    display: flex;
    align-items: center;
    gap: rem(8px);
    padding: rem(4px) 0;
    width: max-content;
  }

  &__swatch {
    flex-shrink: 0;
    box-sizing: border-box;
    border: 2px solid transparent;
    background-color: var(--swatch);
    background-clip: padding-box;
    cursor: pointer;
    padding: 0;

    &--light {
      border-color: var(--color-surface-placeholder);
    }

    &--selected {
      border-color: var(--color-foreground-primary);
    }
  }

  &__more {
    flex-shrink: 0;

    :deep(.base-dropdown-wrapper--placement-bottom.base-dropdown-wrapper--with-shadow .base-dropdown-wrapper__content) {
      box-shadow: 0 10px 20px -5px rgb(0 0 0 / 10%);
    }
  }

  &__more-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    border: none;
    cursor: pointer;
    background-color: var(--color-surface-placeholder);
  }

  &__more-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
    transition: transform 0.2s ease;

    &--open {
      transform: rotate(180deg);
    }
  }

  &__palette {
    display: grid;
    gap: rem(8px);
    padding: rem(4px);
    justify-content: center;
  }

  &__palette-item {
    flex-shrink: 0;
    box-sizing: border-box;
    border: 2px solid transparent;
    background-color: var(--swatch);
    background-clip: padding-box;
    cursor: pointer;
    padding: 0;

    &.base-color-picker__swatch--light {
      border-color: var(--color-surface-placeholder);
    }

    &.base-color-picker__swatch--selected {
      border-color: var(--color-foreground-primary);
    }
  }

  &__error {
    margin-top: rem(6px);
  }

  &--size-md {
    .base-color-picker__swatch,
    .base-color-picker__palette-item,
    .base-color-picker__more-btn {
      width: rem(32px);
      height: rem(32px);
    }

    .base-color-picker__swatch,
    .base-color-picker__palette-item {
      border-radius: rem(8px);
    }

    .base-color-picker__more-btn {
      border-radius: rem(8px);
    }

    .base-color-picker__palette {
      grid-template-columns: repeat(6, rem(32px));
    }
  }

  &--size-lg {
    .base-color-picker__swatch,
    .base-color-picker__palette-item,
    .base-color-picker__more-btn {
      width: rem(38px);
      height: rem(38px);
    }

    .base-color-picker__swatch,
    .base-color-picker__palette-item {
      border-radius: rem(12px);
    }

    .base-color-picker__more-btn {
      border-radius: rem(12px);
    }

    .base-color-picker__palette {
      grid-template-columns: repeat(6, rem(38px));
    }
  }

  &--variant-light .base-color-picker__row {
    background-color: var(--color-surface-bg);
  }

  &--variant-secondary .base-color-picker__row {
    background-color: var(--color-surface-placeholder);
  }

  &--variant-secondary .base-color-picker__more-btn {
    background-color: var(--color-surface-bg);
  }

  &--size-md#{&}--rounded {
    .base-color-picker__row {
      height: rem(44px);
      min-height: rem(44px);
      padding: rem(8px);
      border-radius: 9999px;
      box-sizing: border-box;
    }

    .base-color-picker__scroll {
      display: flex;
      align-items: center;
      min-height: 0;
    }

    .base-color-picker__swatches {
      padding: 0;
      align-items: center;
    }

    .base-color-picker__swatch,
    .base-color-picker__palette-item,
    .base-color-picker__more-btn {
      width: rem(28px);
      height: rem(28px);
      border-radius: 50%;
    }

    .base-color-picker__palette {
      grid-template-columns: repeat(6, rem(28px));
    }
  }

  &--size-lg#{&}--rounded {
    .base-color-picker__row {
      height: rem(54px);
      min-height: rem(54px);
      padding: rem(8px);
      border-radius: 9999px;
      box-sizing: border-box;
    }

    .base-color-picker__scroll {
      display: flex;
      align-items: center;
      min-height: 0;
    }

    .base-color-picker__swatches {
      padding: 0;
      align-items: center;
    }

    .base-color-picker__swatch,
    .base-color-picker__palette-item,
    .base-color-picker__more-btn {
      width: rem(38px);
      height: rem(38px);
      border-radius: 50%;
    }

    .base-color-picker__palette {
      grid-template-columns: repeat(6, rem(38px));
    }
  }
}
</style>
